import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import CallIcon from "@mui/icons-material/Call";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneMissedIcon from "@mui/icons-material/PhoneMissed";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerIcon from "@mui/icons-material/Timer";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getAuthToken } from "../../utils/getAuthToken";
import CallsHeatmap from "./CallsHeatmap";
import CallsDateRangePicker from "./CallsDateRangePicker";
import { PALETTE, formatDuration, formatHoursMinutes, formatCount } from "./callsPalette";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
}

async function fetchStats(startDate, endDate) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}/api/calling/stats`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { startDate, endDate },
  });
  return res.data;
}

/** A solid-fill KPI tile — value is the hero figure, label rides underneath it. */
function BigStatTile({ label, value, icon, fill }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{ flex: "1 1 180px", minWidth: 160 }}
    >
      <Box
        sx={{
          borderRadius: 2.5,
          p: 2.5,
          bgcolor: fill,
          color: "#fff",
          height: "100%",
          boxSizing: "border-box",
          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, opacity: 0.95 }}>
          {icon}
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
            {label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
      </Box>
    </motion.div>
  );
}

/** A neutral info tile — same grid rhythm as BigStatTile, no status color (nothing here is good/bad, just a count). */
function InfoTile({ label, value, icon }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: "1 1 180px",
        minWidth: 160,
        borderRadius: 2.5,
        p: 2.5,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, color: "text.secondary" }}>
        {icon}
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 26, fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
}

/** Animated ring — an SVG donut, not a MUI CircularProgress, so the stroke
 * width/color/track are fully controlled and match the rest of this page's
 * design system rather than Material's default look. */
function Ring({ pct, color, size = 64, stroke = 8 }) {
  const theme = useTheme();
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct));
  const track = theme.palette.mode === "dark" ? "#2c2c2a" : "#e1e0d9";

  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        initial={{ strokeDashoffset: circumference }}
        animate={{ strokeDashoffset: circumference - (clamped / 100) * circumference }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      />
    </svg>
  );
}

/** Rate card: big % as the hero figure (kept in plain text ink — the ring
 * beside it is what carries the status color, per the "text never wears
 * the data color" rule; the ring is a colored mark, not the number itself). */
function RateCard({ label, pct, color }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: "1 1 220px",
        minWidth: 200,
        borderRadius: 2.5,
        p: 2.5,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1 }}>{pct}%</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", mt: 0.5 }}>
          {label}
        </Typography>
      </Box>
      <Ring pct={pct} color={color} />
    </Box>
  );
}

// Outbound call-time cap for this billing cycle (see calling.usage.js /
// usageTrackingService.js's "callMinutesOutbound" feature) — never gates or affects
// a call already in progress, only whether a *new* one can start. Rendered
// only when a real cap is configured: with no limit set there's nothing
// actionable to show, so the card is omitted rather than displaying a
// "0m 0s used — unlimited plan" line that's just noise on every load.
function CallUsageMeter({ usedSeconds, limitSeconds }) {
  const theme = useTheme();
  if (!limitSeconds || limitSeconds <= 0) return null;

  const pct = Math.min(100, Math.round((usedSeconds / limitSeconds) * 100));
  // > not >=, to match the backend gate (enforceUsageLimit with amount: 0
  // blocks once used exceeds limit, not the instant it's reached) — see
  // assertCallTimeWithinLimit in callingController.js.
  const isOver = usedSeconds > limitSeconds;
  const isNear = !isOver && pct >= 90;
  const fillColor = isOver ? PALETTE.critical.ring : isNear ? PALETTE.warning.ring : PALETTE.good.ring;
  const track = theme.palette.mode === "dark" ? "#2c2c2a" : "#e1e0d9";

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        p: 2.5,
        mb: 3,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Business call time this cycle</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          {formatHoursMinutes(usedSeconds)} of {formatHoursMinutes(limitSeconds)} ({pct}%)
        </Typography>
      </Box>

      <Box sx={{ mt: 1.5, height: 8, borderRadius: 4, bgcolor: track, overflow: "hidden" }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{ height: "100%", borderRadius: 4, background: fillColor }}
        />
      </Box>

      {isOver && (
        <Typography sx={{ fontSize: 12.5, color: PALETTE.critical.ring, mt: 1 }}>
          Over limit for this cycle — new calls are blocked until it rolls over or the plan is upgraded. Calls
          already in progress are never disconnected.
        </Typography>
      )}
      {isNear && (
        <Typography sx={{ fontSize: 12.5, color: "#9a6c0c", mt: 1 }}>
          Approaching this cycle's call-time limit.
        </Typography>
      )}
    </Box>
  );
}

function CallVolumeChart({ title, data }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        p: 2,
        mb: 3,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>{title}</Typography>
      <Box sx={{ width: "100%", height: 220 }}>
        {/* minWidth/minHeight give Recharts a real size to measure on the very
            first layout pass — without them, ResponsiveContainer can observe a
            transient 0x0 (this Box's flex ancestors haven't settled yet) and warn. */}
        <ResponsiveContainer minWidth={0} minHeight={220}>
          <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="answered" name="Answered" fill={PALETTE.good.ring} radius={[3, 3, 0, 0]} />
            <Bar dataKey="missed" name="Missed" fill={PALETTE.critical.ring} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

function ErrorState({ onRetry }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 8 }}>
      <ErrorOutlineIcon sx={{ fontSize: 32, color: PALETTE.critical.ring }} />
      <Typography sx={{ fontWeight: 700 }}>Couldn't load this report</Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        Check your connection and try again.
      </Typography>
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          border: "none",
          bgcolor: PALETTE.blue.fill,
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <RefreshIcon sx={{ fontSize: 16 }} />
        Retry
      </Box>
    </Box>
  );
}

export default function CallsOverview() {
  const theme = useTheme();
  const [range, setRange] = useState(defaultRange);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchStats(range.start, range.end);
      setStats(data);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = stats?.totals;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Overview
        </Typography>
        <CallsDateRangePicker value={range} onChange={setRange} />
      </Box>

      {loading && !stats ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error && !stats ? (
        <ErrorState onRetry={load} />
      ) : (
        <>
          {/* Outcome breakdown — the four numbers a manager checks first. */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <BigStatTile label="Total calls" value={formatCount(totals?.totalCalls)} icon={<CallIcon fontSize="small" />} fill={PALETTE.blue.fill} />
            <BigStatTile label="Answered" value={formatCount(totals?.answered)} icon={<CheckCircleIcon fontSize="small" />} fill={PALETTE.good.fill} />
            <BigStatTile label="Missed" value={formatCount(totals?.missed)} icon={<PhoneMissedIcon fontSize="small" />} fill={PALETTE.critical.fill} />
            <BigStatTile label="Abandoned" value={formatCount(totals?.abandoned)} icon={<PhoneDisabledIcon fontSize="small" />} fill={PALETTE.serious.fill} />
          </Box>

          {/* Talk-time context, plus the incoming/outgoing split the charts below break down further. */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <InfoTile label="Incoming" value={formatCount(totals?.incoming)} icon={<CallIcon fontSize="small" />} />
            <InfoTile label="Outgoing" value={formatCount(totals?.outgoing)} icon={<CallIcon fontSize="small" />} />
            <InfoTile label="Avg talk time" value={formatDuration(totals?.avgDurationSeconds)} icon={<AccessTimeIcon fontSize="small" />} />
            <InfoTile label="Total talk time" value={formatHoursMinutes(totals?.totalTalkSeconds)} icon={<TimerIcon fontSize="small" />} />
          </Box>

          {/* Rates — same numbers as the tiles above, framed as a share of all calls. */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <RateCard label="Answer rate" pct={totals?.answerRate ?? 0} color={PALETTE.good.ring} />
            <RateCard label="Miss rate" pct={totals?.missRate ?? 0} color={PALETTE.critical.ring} />
            <RateCard label="Abandon rate" pct={totals?.abandonRate ?? 0} color={PALETTE.serious.ring} />
          </Box>

          <CallUsageMeter usedSeconds={stats?.callUsage?.usedSeconds ?? 0} limitSeconds={stats?.callUsage?.limitSeconds ?? 0} />

          <CallVolumeChart title="Incoming calls — answered vs. missed" data={stats?.incomingByDay || []} />
          <CallVolumeChart title="Outgoing calls — answered vs. missed" data={stats?.outgoingByDay || []} />

          <Box
            sx={{
              borderRadius: 2.5,
              p: 2,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Weekly call volume by hour</Typography>
            <CallsHeatmap data={stats?.heatmap || []} />
          </Box>
        </>
      )}
    </Box>
  );
}
